import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Flame, Users, Calendar, TrendingUp, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import PromoteProfileModal from './PromoteProfileModal';
import { usePT } from '../utils/promoteTranslations';

/**
 * "Promote my profile" settings section — shows live promotion status,
 * remaining days, followers gained since promotion, plus a history block.
 * Auto-handles the Stripe return-from-checkout via `?promo=success&session_id=...`.
 */
const PromoteProfileSection = () => {
  const t = usePT();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/promote-profile/status');
      setStatus(res.data);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Consume Stripe return once
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get('session_id');
    if (params.get('promo') === 'success' && sid) {
      (async () => {
        try {
          const res = await axios.post('/api/promote-profile/confirm', { session_id: sid });
          if (res.data.paid || res.data.already_active) {
            toast.success(t('promote.successToast', { date: (res.data.ends_at || '').split('T')[0] || '' }));
            window.history.replaceState({}, '', window.location.pathname);
            load();
          }
        } catch (_) { /* ignore */ }
      })();
    } else if (params.get('promo') === 'cancelled') {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  return (
    <div data-testid="promote-profile-panel">
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 rounded-full bg-gradient-to-br from-orange-500 to-magenta-600">
          <Flame className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-semibold">{t('promote.settingsSectionTitle')}</h2>
          <p className="text-xs text-gray-400">{t('promote.settingsSectionDesc')}</p>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-gray-400 py-4">…</p>
      ) : status?.active ? (
        <div className="mt-4 p-4 rounded-2xl bg-gradient-to-br from-orange-500/10 to-magenta-500/10 border border-orange-500/30" data-testid="promote-active-block">
          <p className="text-orange-300 font-semibold mb-3 flex items-center gap-2">
            <Check className="w-4 h-4" /> {t('promote.activePromoLabel')}
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-xl bg-white/5 border border-white/10">
              <div className="flex items-center gap-2 text-xs text-gray-400 mb-1"><Calendar className="w-3 h-3" /> {t('promote.duration')}</div>
              <p className="text-lg font-bold">{t('promote.daysLeft', { n: status.days_left ?? '-' })}</p>
            </div>
            <div className="p-3 rounded-xl bg-white/5 border border-white/10">
              <div className="flex items-center gap-2 text-xs text-gray-400 mb-1"><Users className="w-3 h-3" /> {t('promote.followers')}</div>
              <p className="text-lg font-bold text-emerald-400">+{status.followers_gained ?? 0}</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-4">
          <p className="text-sm text-gray-400 mb-3">{t('promote.noActivePromo')}</p>
          <button
            onClick={() => setShowModal(true)}
            className="w-full px-4 py-3 rounded-full bg-gradient-to-r from-orange-500 to-magenta-600 hover:from-orange-600 hover:to-magenta-700 font-semibold flex items-center justify-center gap-2"
            data-testid="promote-start-btn"
          >
            <Flame className="w-4 h-4" /> {t('promote.startPromo')}
          </button>
        </div>
      )}

      {status?.history && status.history.length > 0 && (
        <div className="mt-4">
          <p className="text-sm font-semibold text-gray-300 mb-2">{t('promote.history')}</p>
          <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
            {status.history.map((h) => (
              <div key={h.id} className="p-3 rounded-xl bg-white/5 border border-white/10 text-xs flex items-center justify-between">
                <div>
                  <p className="font-mono text-gray-300">{(h.created_at || '').slice(0, 10)}</p>
                  <p className="text-gray-500">{h.status}</p>
                </div>
                <div className="text-right">
                  <p className="text-orange-300 font-semibold">{h.amount_eur}€</p>
                  <p className="text-gray-500">{h.duration_days || 7}j</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showModal && (
        <PromoteProfileModal onClose={() => setShowModal(false)} />
      )}
    </div>
  );
};

export default PromoteProfileSection;
